import { MoreOutlined } from '@ant-design/icons';
import {
  Dropdown,
  MenuProps,
  notification,
  Popconfirm,
  TableProps,
} from 'antd';
import DOMPurify from 'dompurify';
import React, { useState } from 'react';
import { useParams } from 'react-router';

import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import {
  useDeleteVariantCommentMutation,
  useGetVariantCommentsQuery,
} from '../../api/variantApi';
import { NOTIFICATION_MESSAGES } from '../constants';
import { VariantCommentType } from '../redux';
import CommentEditModal from './CommentEditModal';

const VariantCommentsTable = () => {
  const params = useParams();
  const [deleteVariantComment] = useDeleteVariantCommentMutation();
  // Read comments straight from the query hook's cache rather than a selector
  const { data: variantComments } = useGetVariantCommentsQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const [editingComment, setEditingComment] =
    useState<VariantCommentType | null>(null);

  const commentRenderer = (_: string, record: VariantCommentType) => (
    <div>
      {record.last_edited && (
        <div className="variant-comments-table__edited-hint">
          edited on {record.last_edited}
        </div>
      )}
      <div
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(record.notes) }}
      />
    </div>
  );

  const handleDelete = async (
    commentId: number,
    variantId?: string | number,
  ) => {
    try {
      await deleteVariantComment({
        comment_id: commentId,
        variant_id: variantId,
      }).unwrap();
      notification.success({
        message: NOTIFICATION_MESSAGES.SUCCESS,
        description: NOTIFICATION_MESSAGES.COMMENT_DELETED,
      });
    } catch (e) {
      console.error(e);
      notification.error({
        message: NOTIFICATION_MESSAGES.ERROR,
        description: NOTIFICATION_MESSAGES.COMMENT_DELETE_FAILED,
      });
    }
  };

  const getMenuItems = (record: VariantCommentType): MenuProps['items'] => [
    {
      key: 'edit',
      label: 'Edit',
      onClick: () => setEditingComment(record),
    },
    {
      key: 'delete',
      label: (
        <Popconfirm
          title="Are you sure you want to delete this comment?"
          onConfirm={() => handleDelete(record.id, params.vid)}
          okText="Yes"
          cancelText="No"
          placement="left"
        >
          <span>Delete</span>
        </Popconfirm>
      ),
    },
  ];

  const columns: TableProps<VariantCommentType>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'created',
      key: 'created',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'By',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Comments on',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: commentRenderer,
    },
    {
      key: 'actions',
      width: 40,
      render: (_: unknown, record: VariantCommentType) => {
        if (!record.is_creator) {
          return null;
        }
        return (
          <Dropdown
            menu={{ items: getMenuItems(record) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <MoreOutlined
              style={{ fontSize: 18, cursor: 'pointer', padding: '0 4px' }}
            />
          </Dropdown>
        );
      },
    },
  ];

  if (!variantComments || variantComments.length === 0) {
    return null; // Do not render the table if there's no data
  }

  return (
    <>
      <ResizableTable
        columns={columns}
        rowKey={(record) => `${record.id}-${record.created}`}
        dataSource={variantComments}
        pagination={false}
        persistenceKey="variant-comments"
      />
      {editingComment && (
        <CommentEditModal
          comment={editingComment}
          onClose={() => setEditingComment(null)}
        />
      )}
    </>
  );
};

export default VariantCommentsTable;
